import { useState, useEffect, useCallback, useRef } from 'react';

export type ChatTag = 'değişim/iade' | 'soru' | 'toptancı';

export interface ChatMessage {
  id: string;
  conversationId: string;
  sender: 'customer' | 'admin';
  text: string;
  imageUrl?: string;
  timestamp: number;
  read: boolean;
}

export interface ChatConversation {
  id: string;
  customerName: string;
  startedAt: number;
  lastMessageAt: number;
  isAnswered: boolean;
  tags: ChatTag[];
  messages: ChatMessage[];
  unreadCount: number;
  callState?: 'idle' | 'calling' | 'connected' | 'ended' | 'busy' | 'admin-calling';
  callOffer?: string;
  callAnswer?: string;
  callIceCandidates?: string[];
  adminIceCandidates?: string[];
  // Admin-initiated call fields
  adminCallOffer?: string;
  adminCallAnswer?: string;
  adminCallIceCandidates?: string[];
  customerIceCandidatesForAdmin?: string[];
  // Typing indicator
  adminTyping?: boolean;
}

const STORAGE_KEY = 'fentybarefoot_chats';

function loadConversations(): ChatConversation[] {
  try {
    const data = localStorage.getItem(STORAGE_KEY);
    return data ? JSON.parse(data) : [];
  } catch (_e) {
    return [];
  }
}

function saveConversations(convs: ChatConversation[]): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(convs));
}

function generateId(): string {
  return Math.random().toString(36).slice(2) + Date.now().toString(36);
}

// Customer-side hook
export function useCustomerChat(conversationId: string | null) {
  const [conversation, setConversation] = useState<ChatConversation | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const refresh = useCallback(() => {
    if (!conversationId) return;
    const convs = loadConversations();
    const found = convs.find(c => c.id === conversationId) || null;
    setConversation(found);
  }, [conversationId]);

  useEffect(() => {
    refresh();
    const handler = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY) refresh();
    };
    window.addEventListener('storage', handler);
    const interval = setInterval(refresh, 1000);
    return () => {
      window.removeEventListener('storage', handler);
      clearInterval(interval);
    };
  }, [refresh]);

  const createConversation = useCallback((name: string): string => {
    const convs = loadConversations();
    const newConv: ChatConversation = {
      id: generateId(),
      customerName: name || 'Misafir',
      startedAt: Date.now(),
      lastMessageAt: Date.now(),
      isAnswered: false,
      tags: [],
      messages: [],
      unreadCount: 0,
      callState: 'idle',
    };
    convs.push(newConv);
    saveConversations(convs);
    return newConv.id;
  }, []);

  const sendMessage = useCallback((text: string, imageUrl?: string) => {
    if (!conversationId) return;
    setIsLoading(true);
    const convs = loadConversations();
    const idx = convs.findIndex(c => c.id === conversationId);
    if (idx === -1) { setIsLoading(false); return; }
    const msg: ChatMessage = {
      id: generateId(),
      conversationId,
      sender: 'customer',
      text,
      imageUrl,
      timestamp: Date.now(),
      read: false,
    };
    convs[idx].messages.push(msg);
    convs[idx].lastMessageAt = Date.now();
    convs[idx].unreadCount += 1;
    saveConversations(convs);
    setConversation({ ...convs[idx] });
    setIsLoading(false);
  }, [conversationId]);

  return { conversation, isLoading, createConversation, sendMessage, refresh };
}

// Admin-side hook
export function useAdminChat() {
  const [conversations, setConversations] = useState<ChatConversation[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const refresh = useCallback(() => {
    setConversations(loadConversations());
  }, []);

  useEffect(() => {
    refresh();
    const handler = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY) refresh();
    };
    window.addEventListener('storage', handler);
    const interval = setInterval(refresh, 800);
    return () => {
      window.removeEventListener('storage', handler);
      clearInterval(interval);
    };
  }, [refresh]);

  const sendAdminMessage = useCallback((convId: string, text: string, imageUrl?: string) => {
    const convs = loadConversations();
    const idx = convs.findIndex(c => c.id === convId);
    if (idx === -1) return;
    const msg: ChatMessage = {
      id: generateId(),
      conversationId: convId,
      sender: 'admin',
      text,
      imageUrl,
      timestamp: Date.now(),
      read: true,
    };
    convs[idx].messages.push(msg);
    convs[idx].lastMessageAt = Date.now();
    convs[idx].isAnswered = true;
    convs[idx].unreadCount = 0;
    convs[idx].adminTyping = false;
    saveConversations(convs);
    setConversations([...convs]);
  }, []);

  const markAsRead = useCallback((convId: string) => {
    const convs = loadConversations();
    const idx = convs.findIndex(c => c.id === convId);
    if (idx === -1) return;
    convs[idx].unreadCount = 0;
    convs[idx].messages = convs[idx].messages.map(m => ({ ...m, read: true }));
    saveConversations(convs);
    setConversations([...convs]);
  }, []);

  const addTag = useCallback((convId: string, tag: ChatTag) => {
    const convs = loadConversations();
    const idx = convs.findIndex(c => c.id === convId);
    if (idx === -1) return;
    if (!convs[idx].tags.includes(tag)) {
      convs[idx].tags.push(tag);
      saveConversations(convs);
      setConversations([...convs]);
    }
  }, []);

  const removeTag = useCallback((convId: string, tag: ChatTag) => {
    const convs = loadConversations();
    const idx = convs.findIndex(c => c.id === convId);
    if (idx === -1) return;
    convs[idx].tags = convs[idx].tags.filter(t => t !== tag);
    saveConversations(convs);
    setConversations([...convs]);
  }, []);

  const markAnswered = useCallback((convId: string, answered: boolean) => {
    const convs = loadConversations();
    const idx = convs.findIndex(c => c.id === convId);
    if (idx === -1) return;
    convs[idx].isAnswered = answered;
    saveConversations(convs);
    setConversations([...convs]);
  }, []);

  const updateCallState = useCallback((convId: string, updates: Partial<ChatConversation>) => {
    const convs = loadConversations();
    const idx = convs.findIndex(c => c.id === convId);
    if (idx === -1) return;
    Object.assign(convs[idx], updates);
    saveConversations(convs);
    setConversations([...convs]);
  }, []);

  const setAdminTyping = useCallback((convId: string, typing: boolean) => {
    const convs = loadConversations();
    const idx = convs.findIndex(c => c.id === convId);
    if (idx === -1) return;
    if (convs[idx].adminTyping === typing) return;
    convs[idx].adminTyping = typing;
    saveConversations(convs);
    setConversations([...convs]);
  }, []);

  const selected = conversations.find(c => c.id === selectedId) || null;

  return {
    conversations,
    selectedId,
    setSelectedId,
    selected,
    sendAdminMessage,
    markAsRead,
    addTag,
    removeTag,
    markAnswered,
    updateCallState,
    setAdminTyping,
    refresh,
  };
}

// WebRTC Voice Call helpers
export function useVoiceCall(
  conversationId: string | null,
  role: 'customer' | 'admin',
  onStateChange?: (state: string) => void
) {
  const pcRef = useRef<RTCPeerConnection | null>(null);
  const localStreamRef = useRef<MediaStream | null>(null);
  const [callState, setCallState] = useState<'idle' | 'calling' | 'ringing' | 'connected' | 'ended'>('idle');
  const [remoteAudio] = useState<HTMLAudioElement>(() => new Audio());

  const updateState = useCallback((s: typeof callState) => {
    setCallState(s);
    onStateChange?.(s);
  }, [onStateChange]);

  const createPC = useCallback(() => {
    const pc = new RTCPeerConnection({
      iceServers: [
        { urls: 'stun:stun.l.google.com:19302' },
        { urls: 'stun:stun1.l.google.com:19302' },
      ],
    });
    pc.ontrack = (e) => {
      remoteAudio.srcObject = e.streams[0];
      remoteAudio.play().catch(() => {});
    };
    pcRef.current = pc;
    return pc;
  }, [remoteAudio]);

  // ── Admin initiates a call TO the customer ───────────────────────────────
  const initiateFromAdmin = useCallback(async () => {
    if (!conversationId) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      localStreamRef.current = stream;
      const pc = createPC();
      stream.getTracks().forEach(t => pc.addTrack(t, stream));

      const adminCands: string[] = [];
      pc.onicecandidate = (e) => {
        if (e.candidate) {
          adminCands.push(JSON.stringify(e.candidate));
          const c2 = loadConversations();
          const i2 = c2.findIndex(c => c.id === conversationId);
          if (i2 !== -1) {
            c2[i2].adminCallIceCandidates = [...adminCands];
            saveConversations(c2);
          }
        }
      };

      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);

      const convs = loadConversations();
      const idx = convs.findIndex(c => c.id === conversationId);
      if (idx !== -1) {
        convs[idx].callState = 'admin-calling';
        convs[idx].adminCallOffer = JSON.stringify(offer);
        convs[idx].adminCallAnswer = undefined;
        convs[idx].adminCallIceCandidates = [];
        convs[idx].customerIceCandidatesForAdmin = [];
        saveConversations(convs);
      }
      updateState('calling');
    } catch (_err) {
      updateState('idle');
    }
  }, [conversationId, createPC, updateState]);

  // ── Customer accepts admin-initiated call ────────────────────────────────
  const acceptFromAdmin = useCallback(async () => {
    if (!conversationId) return;
    try {
      const convs = loadConversations();
      const idx = convs.findIndex(c => c.id === conversationId);
      if (idx === -1 || !convs[idx].adminCallOffer) return;
      const offer = JSON.parse(convs[idx].adminCallOffer!);

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      localStreamRef.current = stream;
      const pc = createPC();
      stream.getTracks().forEach(t => pc.addTrack(t, stream));

      const custCands: string[] = [];
      pc.onicecandidate = (e) => {
        if (e.candidate) {
          custCands.push(JSON.stringify(e.candidate));
          const c2 = loadConversations();
          const i2 = c2.findIndex(c => c.id === conversationId);
          if (i2 !== -1) {
            c2[i2].customerIceCandidatesForAdmin = [...custCands];
            saveConversations(c2);
          }
        }
      };

      await pc.setRemoteDescription(new RTCSessionDescription(offer));
      const answer = await pc.createAnswer();
      await pc.setLocalDescription(answer);

      const convs2 = loadConversations();
      const idx2 = convs2.findIndex(c => c.id === conversationId);
      if (idx2 !== -1) {
        convs2[idx2].callState = 'connected';
        convs2[idx2].adminCallAnswer = JSON.stringify(answer);
        saveConversations(convs2);
      }
      updateState('connected');

      // Add admin's ICE candidates
      const adminCands = convs[idx].adminCallIceCandidates || [];
      for (const c of adminCands) {
        try { await pc.addIceCandidate(new RTCIceCandidate(JSON.parse(c))); } catch (_e) { /* ignore */ }
      }
    } catch (_err) {
      updateState('idle');
    }
  }, [conversationId, createPC, updateState]);

  const initiate = useCallback(async () => {
    if (!conversationId) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      localStreamRef.current = stream;
      const pc = createPC();
      stream.getTracks().forEach(t => pc.addTrack(t, stream));

      const candidates: string[] = [];
      pc.onicecandidate = (e) => {
        if (e.candidate) {
          candidates.push(JSON.stringify(e.candidate));
          const convs = loadConversations();
          const idx = convs.findIndex(c => c.id === conversationId);
          if (idx !== -1) {
            convs[idx].callIceCandidates = [...candidates];
            saveConversations(convs);
          }
        }
      };

      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);

      const convs = loadConversations();
      const idx = convs.findIndex(c => c.id === conversationId);
      if (idx !== -1) {
        convs[idx].callState = 'calling';
        convs[idx].callOffer = JSON.stringify(offer);
        convs[idx].callAnswer = undefined;
        convs[idx].callIceCandidates = [];
        convs[idx].adminIceCandidates = [];
        saveConversations(convs);
      }
      updateState('calling');
    } catch (_err) {
      updateState('idle');
    }
  }, [conversationId, createPC, updateState]);

  const accept = useCallback(async () => {
    if (!conversationId) return;
    try {
      const convs = loadConversations();
      const idx = convs.findIndex(c => c.id === conversationId);
      if (idx === -1 || !convs[idx].callOffer) return;
      const offer = JSON.parse(convs[idx].callOffer!);

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      localStreamRef.current = stream;
      const pc = createPC();
      stream.getTracks().forEach(t => pc.addTrack(t, stream));

      const adminCandidates: string[] = [];
      pc.onicecandidate = (e) => {
        if (e.candidate) {
          adminCandidates.push(JSON.stringify(e.candidate));
          const c2 = loadConversations();
          const i2 = c2.findIndex(c => c.id === conversationId);
          if (i2 !== -1) {
            c2[i2].adminIceCandidates = [...adminCandidates];
            saveConversations(c2);
          }
        }
      };

      await pc.setRemoteDescription(new RTCSessionDescription(offer));
      const answer = await pc.createAnswer();
      await pc.setLocalDescription(answer);

      const convs2 = loadConversations();
      const idx2 = convs2.findIndex(c => c.id === conversationId);
      if (idx2 !== -1) {
        convs2[idx2].callState = 'connected';
        convs2[idx2].callAnswer = JSON.stringify(answer);
        saveConversations(convs2);
      }

      updateState('connected');

      const callerCandidates = convs[idx].callIceCandidates || [];
      for (const c of callerCandidates) {
        try { await pc.addIceCandidate(new RTCIceCandidate(JSON.parse(c))); } catch (_e) { /* ignore */ }
      }
    } catch (_err) {
      updateState('idle');
    }
  }, [conversationId, createPC, updateState]);

  const completeConnection = useCallback(async () => {
    if (!conversationId || role !== 'customer') return;
    const pc = pcRef.current;
    if (!pc) return;
    try {
      const convs = loadConversations();
      const idx = convs.findIndex(c => c.id === conversationId);
      if (idx === -1 || !convs[idx].callAnswer) return;
      const answer = JSON.parse(convs[idx].callAnswer!);
      if (pc.signalingState === 'have-local-offer') {
        await pc.setRemoteDescription(new RTCSessionDescription(answer));
        updateState('connected');
        const adminCands = convs[idx].adminIceCandidates || [];
        for (const c of adminCands) {
          try { await pc.addIceCandidate(new RTCIceCandidate(JSON.parse(c))); } catch (_e) { /* ignore */ }
        }
      }
    } catch (_err) { /* ignore */ }
  }, [conversationId, role, updateState]);

  const hangup = useCallback(() => {
    pcRef.current?.close();
    pcRef.current = null;
    localStreamRef.current?.getTracks().forEach(t => t.stop());
    localStreamRef.current = null;
    remoteAudio.srcObject = null;
    if (conversationId) {
      const convs = loadConversations();
      const idx = convs.findIndex(c => c.id === conversationId);
      if (idx !== -1) {
        convs[idx].callState = 'idle';
        convs[idx].callOffer = undefined;
        convs[idx].callAnswer = undefined;
        convs[idx].adminCallOffer = undefined;
        convs[idx].adminCallAnswer = undefined;
        saveConversations(convs);
      }
    }
    updateState('idle');
  }, [conversationId, remoteAudio, updateState]);

  // Poll for answer (customer-initiated call, customer side)
  useEffect(() => {
    if (role !== 'customer' || callState !== 'calling' || !conversationId) return;
    const interval = setInterval(async () => {
      const convs = loadConversations();
      const conv = convs.find(c => c.id === conversationId);
      if (conv?.callAnswer && pcRef.current?.signalingState === 'have-local-offer') {
        clearInterval(interval);
        await completeConnection();
      }
      if (conv?.callState === 'idle') {
        clearInterval(interval);
        hangup();
      }
    }, 1000);
    return () => clearInterval(interval);
  }, [role, callState, conversationId, completeConnection, hangup]);

  // Poll for admin-initiated call answer (admin side completes WebRTC)
  useEffect(() => {
    if (role !== 'admin' || callState !== 'calling' || !conversationId) return;
    const interval = setInterval(async () => {
      const convs = loadConversations();
      const conv = convs.find(c => c.id === conversationId);
      if (conv?.adminCallAnswer && pcRef.current?.signalingState === 'have-local-offer') {
        clearInterval(interval);
        const answer = JSON.parse(conv.adminCallAnswer);
        try {
          await pcRef.current!.setRemoteDescription(new RTCSessionDescription(answer));
          updateState('connected');
          const custCands = conv.customerIceCandidatesForAdmin || [];
          for (const c of custCands) {
            try { await pcRef.current!.addIceCandidate(new RTCIceCandidate(JSON.parse(c))); } catch (_e) { /* ignore */ }
          }
        } catch (_e) { /* ignore */ }
      }
      if (conv?.callState === 'idle') {
        clearInterval(interval);
        hangup();
      }
    }, 1000);
    return () => clearInterval(interval);
  }, [role, callState, conversationId, updateState, hangup]);

  return { callState, initiate, initiateFromAdmin, accept, acceptFromAdmin, hangup };
}
